<script setup>
import { ref, watch } from 'vue';
import SalesAPI from 'dashboard/api/sales';
import NonSalesAPI from 'dashboard/api/nonSales';
import FollowUpsAPI from 'dashboard/api/followUps';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const items = ref([]);
const isLoading = ref(false);

const brl = v =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const formatDate = iso => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const STATUS_LABEL = { pending: 'Pendente', done: 'Feito', cancelled: 'Cancelado' };
const STATUS_COLOR = {
  pending: 'text-n-brand',
  done: 'text-n-teal-9',
  cancelled: 'text-n-slate-9 line-through',
};

async function load() {
  if (!props.conversationId) return;
  isLoading.value = true;
  try {
    const [salesRes, nonSalesRes, followUpsRes] = await Promise.all([
      SalesAPI.getAllForConversation(props.conversationId).catch(() => ({ data: [] })),
      NonSalesAPI.getAllForConversation(props.conversationId).catch(() => ({ data: [] })),
      FollowUpsAPI.get({ conversation_id: props.conversationId }).catch(() => ({ data: [] })),
    ]);

    const sales = (salesRes.data || []).map(s => ({
      type: 'sale',
      date: s.created_at,
      icon: 'i-lucide-badge-dollar-sign',
      label: `Venda ${s.status === 'cancelled' ? '(cancelada)' : ''}`,
      detail: `${brl(s.amount)} • Pedido #${s.order_number}`,
      extra: s.cancellation_reason ? `Motivo: ${s.cancellation_reason}` : null,
      colorClass: s.status === 'cancelled' ? 'text-n-slate-9' : 'text-n-teal-9',
    }));

    const nonSales = (nonSalesRes.data || []).map(ns => ({
      type: 'non_sale',
      date: ns.created_at,
      icon: 'i-lucide-x-circle',
      label: 'Não venda',
      detail: ns.reason,
      extra: null,
      colorClass: 'text-n-ruby-9',
    }));

    const followUps = (Array.isArray(followUpsRes.data) ? followUpsRes.data : []).map(f => ({
      type: 'follow_up',
      date: f.created_at,
      icon: 'i-lucide-calendar-clock',
      label: `Retorno agendado para ${formatDate(f.scheduled_for)}`,
      detail: f.source_reason || null,
      extra: f.notes || null,
      statusLabel: STATUS_LABEL[f.status] || f.status,
      statusColor: STATUS_COLOR[f.status] || '',
      colorClass: 'text-n-slate-11',
    }));

    items.value = [...sales, ...nonSales, ...followUps].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  } finally {
    isLoading.value = false;
  }
}

watch(() => props.conversationId, load, { immediate: true });
</script>

<template>
  <div class="flex flex-col gap-2 py-1">
    <div v-if="isLoading" class="flex justify-center py-6">
      <span class="i-lucide-loader-circle animate-spin text-2xl text-n-slate-9" />
    </div>

    <div
      v-else-if="!items.length"
      class="flex flex-col items-center gap-1 py-6 text-n-slate-9"
    >
      <span class="i-lucide-history text-2xl" />
      <p class="text-xs">Nenhum evento registrado.</p>
    </div>

    <div v-else class="relative flex flex-col gap-0">
      <div
        v-for="(item, index) in items"
        :key="index"
        class="flex gap-3 pb-3 last:pb-0"
      >
        <!-- Linha vertical da timeline -->
        <div class="flex flex-col items-center">
          <span :class="[item.icon, item.colorClass, 'text-base shrink-0 mt-0.5']" />
          <div
            v-if="index < items.length - 1"
            class="w-px flex-1 bg-n-weak mt-1"
          />
        </div>

        <!-- Conteúdo -->
        <div class="flex flex-col gap-0.5 min-w-0 pb-1">
          <div class="flex items-center gap-2">
            <span class="text-xs font-medium text-n-slate-12 truncate">{{ item.label }}</span>
            <span
              v-if="item.statusLabel"
              class="text-[10px] shrink-0"
              :class="item.statusColor"
            >
              {{ item.statusLabel }}
            </span>
          </div>
          <span v-if="item.detail" class="text-xs text-n-slate-10 truncate">{{ item.detail }}</span>
          <span v-if="item.extra" class="text-xs text-n-slate-9 italic truncate">{{ item.extra }}</span>
          <span class="text-[10px] text-n-slate-8">{{ formatDate(item.date) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
